import { useCallback, useRef, useState } from 'react'

// Tracks the rendered column count of a grid whose template is
// `repeat(auto-fill, minmax(...))`, so the count is viewport-dependent and only
// knowable at runtime. This lets the gallery trim incomplete trailing rows to
// whatever the layout actually resolved to.
//
// Returns a [callbackRef, columnCount] pair - attach the ref to the grid element.
// A callback ref (not a ref object + effect) is deliberate: the gallery paints a
// loading state first, so the grid node only mounts on a later render. An effect
// keyed on a ref object fires once with `.current` still null and never re-runs
// when the node finally attaches, leaving the count stuck at 0 (no trimming). A
// callback ref instead runs the moment the node mounts (and unmounts), so the
// measurement is correct whether or not the grid is present on first render.
function useGridColumnCount() {
  const [columnCount, setColumnCount] = useState(0)
  const observerRef = useRef(null)
  const elementRef = useRef(null)

  const measure = useCallback(() => {
    const element = elementRef.current
    if (!element) return
    const template = getComputedStyle(element).gridTemplateColumns
    // Computed style expands repeat()/auto-fill to a resolved track list
    // ("227px 227px ..."), so the token count is the column count.
    const count = template && template !== 'none'
      ? template.split(' ').filter(Boolean).length
      : 0
    setColumnCount(count)
  }, [])

  const gridRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    elementRef.current = node
    if (!node || typeof ResizeObserver === 'undefined') return
    measure()
    observerRef.current = new ResizeObserver(measure)
    observerRef.current.observe(node)
  }, [measure])

  return [gridRef, columnCount]
}

export default useGridColumnCount
